"use client";

import { m } from "motion/react";
import { ReactNode } from "react";

interface GlitchTextProps {
  children: ReactNode;
  className?: string;
  style?: React.CSSProperties;
  isGlitching?: boolean; // Control when glitch is active
  duration?: number; // Duration of glitch effect
}

export function GlitchText({
  children,
  className = "",
  style = {},
  isGlitching = false,
  duration = 0.3,
}: GlitchTextProps) {
  // When not glitching, just return the children as-is
  if (!isGlitching) {
    return <>{children}</>;
  }

  return (
    <div className="relative inline-block">
      {/* Main text layer */}
      <span className={className} style={style}>
        {children}
      </span>

      {/* Cyan glitch layer */}
      <m.span
        className={`absolute inset-0 pointer-events-none ${className}`}
        style={{
          color: "#00ffff",
          mixBlendMode: "screen",
        }}
        initial={{ opacity: 0, x: 0 }}
        animate={{
          opacity: [0, 1, 0.8, 0],
          x: [-2, -4, -2, 0],
          textShadow: [
            "0 0 0 transparent",
            "0 0 10px rgba(0, 255, 255, 1)",
            "0 0 20px rgba(0, 255, 255, 0.8)",
            "0 0 0 transparent",
          ],
        }}
        transition={{
          duration: duration,
          times: [0, 0.3, 0.7, 1],
          ease: "easeInOut",
        }}
      >
        {children}
      </m.span>

      {/* Magenta glitch layer */}
      <m.span
        className={`absolute inset-0 pointer-events-none ${className}`}
        style={{
          color: "#ff0080",
          mixBlendMode: "screen",
        }}
        initial={{ opacity: 0, x: 0 }}
        animate={{
          opacity: [0, 1, 0.8, 0],
          x: [2, 4, 2, 0],
          textShadow: [
            "0 0 0 transparent",
            "0 0 10px rgba(255, 0, 128, 1)",
            "0 0 20px rgba(255, 0, 128, 0.8)",
            "0 0 0 transparent",
          ],
        }}
        transition={{
          duration: duration,
          times: [0, 0.3, 0.7, 1],
          ease: "easeInOut",
        }}
      >
        {children}
      </m.span>

      {/* Brief static flash for extra impact */}
      <m.div
        className="absolute inset-0 pointer-events-none"
        style={{
          mixBlendMode: "overlay",
        }}
        initial={{ opacity: 0 }}
        animate={{
          opacity: [0, 0.3, 0],
          background: [
            "transparent",
            "radial-gradient(circle at 50% 50%, rgba(255,255,255,0.1) 0%, transparent 50%)",
            "transparent",
          ],
        }}
        transition={{
          duration: duration * 0.5,
          times: [0, 0.5, 1],
        }}
      />
    </div>
  );
}
